import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@wehunt/core-web'
import type { MapObject } from '@wehunt/ui'
import { listMyMapPins, listMyPinFolders } from './api/myPinCollectionApi'
import type { TeamPinFolderDto } from './api/teamPinFoldersApi'
import { dtoToMapObject } from './mapDtoMappers'
import { resolveImageUrls } from './useGroupMapPins'

/**
 * Shared empty lists, so a query that has not answered yet returns the SAME
 * value every render — see the note in `useGroupMapPins`. A fresh `[]` changes
 * the identity of the team map object the route composes from these, and the
 * map view re-frames the whole ground when that identity changes.
 */
const NO_PINS: MapObject[] = []
const NO_FOLDERS: TeamPinFolderDto[] = []

/**
 * The signed-in hunter's own map pins — the app's private collection, which web
 * has never shown. Most filed pins in production live here rather than in a
 * team's collection (a Tracker waypoint import lands in one of these folders),
 * so a member who organised their pins on the phone found nothing in the
 * browser.
 *
 * Keyed on the uid, not on a team: these pins follow the signed-in hunter across
 * every team they belong to, and caching them per team would refetch the same
 * list once per team and leave stale copies behind on a sign-out.
 */
export function useMyMapPins(): MapObject[] {
  const { state } = useAuth()
  const isSignedIn = state.status === 'signed-in'
  const uid = isSignedIn ? state.user.uid : null

  const { data } = useQuery({
    queryKey: ['clubhouse', 'me', uid, 'map-pins'],
    enabled: isSignedIn,
    queryFn: async () => {
      const token = await (state as Extract<typeof state, { status: 'signed-in' }>).user.getIdToken()
      const dtos = await listMyMapPins(token)
      return Promise.all(
        dtos.map((dto) =>
          // Marked here rather than in the mapper: the DTO is identical to a
          // team pin's, and it is the ROUTE that makes these the viewer's own.
          resolveImageUrls({ ...dtoToMapObject(dto), isPrivate: true }),
        ),
      )
    },
  })

  return data ?? NO_PINS
}

/**
 * The folders of the signed-in hunter's own collection, in stored order. Same
 * shape as a team's folders, read from their `users/{uid}` document instead of a
 * group's — so a caller that groups pins by folder does not care which of the
 * two collections it is looking at.
 */
export function useMyPinFolders(): TeamPinFolderDto[] {
  const { state } = useAuth()
  const isSignedIn = state.status === 'signed-in'
  const uid = isSignedIn ? state.user.uid : null

  const { data } = useQuery({
    queryKey: ['clubhouse', 'me', uid, 'map-pin-folders'],
    enabled: isSignedIn,
    queryFn: async () => {
      const token = await (state as Extract<typeof state, { status: 'signed-in' }>).user.getIdToken()
      return listMyPinFolders(token)
    },
  })

  return data ?? NO_FOLDERS
}
